import { Inject, Injectable } from "@angular/core";
import { Observable } from "rxjs/internal/Observable";
import { map } from "rxjs/operators";
import { IEnvironment } from "../models/interfaces/environment.interface";
import { ListResponse } from "../models/interfaces/list-response.interface";
import { ICreateMailLabelResponse } from "../models/ost/create-mail-label-response.model";
import { OstEstadoEnum } from "../models/ost/enums/ost-estado.enum";
import { OstPaisEnum } from "../models/ost/enums/ost-pais.enum";
import { MailLabelListRs } from "../models/ost/mail-label-list-rs.model";
import { OstExcelRsDto } from "../models/ost/ost-excel-rs.dto";
import { OstHistoricalResponse } from "../models/ost/ost-historical-response.model";
import { OstResponse } from "../models/ost/ost-response.model";
import { OstUpdate } from "../models/ost/ost-update.model";
import { IOstListParams } from "../models/ost/params/ost-list-params.interface";
import { ReqOST } from "../models/ost/req-ost.model";
import { WarrantiesTypeEnum } from "../models/warranties/warranties-type.enum";
import { ENVIRONMENT } from "../modules/keycloak.module";
import { HttpParamsUtil } from "../utils/http-params.util";
import { RetryHttpClient } from "./base/retryable-http.client";

const FIRST_PAGE = 1;
const MAIL_LABELS_PAGE_SIZE = 15;

@Injectable({ providedIn: "root" })
export class OstService extends HttpParamsUtil {
  constructor(
    private readonly httpClient: RetryHttpClient,
    @Inject(ENVIRONMENT) private readonly environment: IEnvironment
  ) {
    super();
  }

  getListOst(
    fechaCreacionDesde: string,
    fechaCreacionHasta: string,
    ost_pais: OstPaisEnum | undefined,
    page: number | undefined,
    pageSize: number | undefined,
    ost_local: string,
    ost_estado?: OstEstadoEnum,
    f11_estado_creacion?: boolean,
    warranty_type_code?: WarrantiesTypeEnum,
    dni?: string
  ): Observable<ListResponse<OstResponse>> {
    const pais = ost_pais ?? OstPaisEnum.CL;
    const params: IOstListParams = {
      fecha_creacion_inicio: fechaCreacionDesde,
      fecha_creacion_final: fechaCreacionHasta,
      ost_pais: pais,
      page: page,
      page_size: pageSize,
      ost_local: `${ost_local}`,
      ost_estado: ost_estado ? ost_estado.toString() : undefined,
      ost_is_store_repair: false,
      f11_estado_creacion: f11_estado_creacion !== undefined ? f11_estado_creacion : undefined,
      warranty_type_code: warranty_type_code !== undefined ? warranty_type_code : undefined,
      cli_num_docu: dni,
    };

    const httpParams = this.toHttpParams(params);

    return this.httpClient.get<ListResponse<OstResponse>>(`${this.environment.BASE_URL}${this.environment.OST["BASE"]}${this.environment.OST["LIST"]}`, {
      params: httpParams,
    });
  }

  gestOstExcel(
    fechaCreacionDesde: string,
    fechaCreacionHasta: string,
    ost_local: string,
    ost_estado: OstEstadoEnum,
    ost_pais: OstPaisEnum = OstPaisEnum.CL
  ): Observable<ListResponse<OstExcelRsDto>> {
    const params: IOstListParams = {
      fecha_creacion_inicio: fechaCreacionDesde,
      fecha_creacion_final: fechaCreacionHasta,
      ost_pais,
      ost_local: `${ost_local}`,
      ost_estado: ost_estado.toString(),
      ost_is_store_repair: false,
    };

    const httpParams = this.toHttpParams(params);

    return this.httpClient.get<ListResponse<OstExcelRsDto>>(`${this.environment.BASE_URL}${this.environment.OST["BASE"]}${this.environment.OST["EXCEL"]}`, {
      params: httpParams,
    });
  }

  private getCreateEndpoint(warrantyType: WarrantiesTypeEnum | undefined): string {
    switch (warrantyType) {
      case WarrantiesTypeEnum.GC:
        return this.environment.OST["CONNECT"];
      case WarrantiesTypeEnum.GE:
        return this.environment.OST["EXTENDED"];
      case WarrantiesTypeEnum.GL:
        return this.environment.OST["LEGAL"];
      case WarrantiesTypeEnum.GPR:
        return this.environment.OST["PROVIDER"];
      default:
        return this.environment.OST["CREATE"];
    }
  }

  createOst(reqOst: ReqOST): Observable<OstResponse> {
    const endpoint = this.getCreateEndpoint(reqOst.prodTipoGarantiaCode as WarrantiesTypeEnum);
    return this.httpClient.post<ReqOST, OstResponse>(`${this.environment.BASE_URL}${this.environment.OST["BASE"]}${endpoint}`, reqOst);
  }

  getOstByNum(ostNum: number, searchType: "OST" | "F11" | "asset", ost_local: string): Observable<OstResponse> {
    const rawParams = { orderNumberType: searchType, ost_local: ost_local };
    const params = this.toHttpParams(rawParams);
    return this.httpClient.get<OstResponse>(`${this.environment.BASE_URL}${this.environment.OST["BASE"]}/${ostNum}`, { params });
  }

  getListOstByDni(dni: string, ost_pais: OstPaisEnum | undefined, page: number | undefined, pageSize: number | undefined): Observable<ListResponse<OstResponse>> {
    const pais = ost_pais ?? OstPaisEnum.CL;
    const rawParams = { cli_num_docu: dni, ost_pais: pais, page, page_size: pageSize };
    const params = this.toHttpParams(rawParams);
    return this.httpClient.get<ListResponse<OstResponse>>(`${this.environment.BASE_URL}${this.environment.OST["BASE"]}${this.environment.OST["LIST"]}`, { params });
  }

  updateOstByNum(ostNum: number, reqOst: OstUpdate): Observable<void> {
    return this.httpClient.patch<OstUpdate, void>(`${this.environment.BASE_URL}${this.environment.OST["BASE"]}/${ostNum}`, reqOst);
  }

  getMailLabels(
    ostLocal: string,
    page: string | number,
    pageSize: string | number,
    fechaGeneracionInicio: string,
    fechaGeneracionFinal: string,
    f11_estado_creacion?: boolean,
    trackingNumber?: string,
    ostNum?: string
  ): Observable<MailLabelListRs> {
    const params = {
      ost_local: ostLocal,
      page: page,
      page_size: pageSize,
      fecha_generacion_inicio: fechaGeneracionInicio,
      fecha_generacion_final: fechaGeneracionFinal,
      tracking_number: trackingNumber,
      f11_estado_creacion: f11_estado_creacion !== undefined ? f11_estado_creacion : undefined,
      ost_num: ostNum,
    };
    const httpParams = this.toHttpParams(params);
    return this.httpClient.get<MailLabelListRs>(`${this.environment.BASE_URL}${this.environment.OST["BASE"]}${this.environment.OST["MAIL_LABELS"]}`, {
      params: httpParams,
    });
  }

  getMailLabelsByOstNum(ostNum: string, ostLocal: string, includePicking = false): Observable<MailLabelListRs> {
    const params = this.toHttpParams({
      ost_num: ostNum,
      ost_local: ostLocal,
      page: FIRST_PAGE,
      page_size: MAIL_LABELS_PAGE_SIZE,
      include_picking: includePicking ? true : undefined,
    });
    return this.httpClient.get<MailLabelListRs>(`${this.environment.BASE_URL}${this.environment.OST["BASE"]}${this.environment.OST["MAIL_LABELS"]}`, {
      params,
    });
  }

  createMailLabel(ostNum: number): Observable<ICreateMailLabelResponse> {
    const url = `${this.environment.BASE_URL}${this.environment.OST["BASE"]}${this.environment.OST["MAIL_LABELS"]}`;
    return this.httpClient.post<{ ostNum: string }, ICreateMailLabelResponse>(url, { ostNum: String(ostNum) });
  }

  getOstHistorical(ostNum: number): Observable<Array<OstHistoricalResponse>> {
    return this.httpClient.get<Array<OstHistoricalResponse>>(`${this.environment.BASE_URL}${this.environment.OST["BASE"]}/${ostNum}${this.environment.OST["HISTORICAL"]}`);
  }

  /**
   * Valida si la boleta tiene una boleta de cambio asociada
   * GET /v1/invoices/changes/valid/{invNumBoleta}
   */
  checkHasBoletaCambio(invNumBoleta: string): Observable<boolean> {
    return this.httpClient
      .get<{
        valid: boolean;
      }>(`${this.environment.BASE_URL}${this.environment.INVOICES["BASE"]}${this.environment.INVOICES["CHANGES_VALID"]}/${invNumBoleta}`)
      .pipe(map((response) => response.valid));
  }

  updateGestionProductoFromExcel(data: {
    items: Array<{ ostNum: number; ostGestionProducto: string; observaciones?: string }>;
  }): Observable<Array<{ ostNum: number; success: boolean; error?: string }>> {
    const url = `${this.environment.BASE_URL}${this.environment.OST["BASE"]}${this.environment.OST["UPDATE_GESTION_PRODUCTO_EXCEL"]}`;
    return this.httpClient.patch<
      { items: Array<{ ostNum: number; ostGestionProducto: string; observaciones?: string }> },
      Array<{ ostNum: number; success: boolean; error?: string }>
    >(url, data);
  }
}
